import { useEffect, useRef, useState } from "react";
// Ordered hero reel. Auto-advances every 5.5s.
const SLIDES = [
  { type: "image", src: "/assets/service/IMG_1314.webp", caption: "Insulated Piping · Retrofit" },
  { type: "image", src: "/assets/service/IMG_2446.webp", caption: "Boiler Plant Service · Long Island" },
  { type: "image", src: "/assets/service/IMG_2458.webp", caption: "After-Hours Response · Emergency Call" },
  { type: "image", src: "/assets/service/IMG_6949.webp", caption: "Anron Fleet · Ready to Roll" },
  { type: "image", src: "/assets/service/IMG_3942.webp", caption: "All-County Crane Set · Rooftop Delivery" },
  { type: "image", src: "/assets/service/IMG_9687.webp", caption: "Grooved Piping Assembly · Field Work" },
  { type: "image", src: "/assets/service/IMG_0513.webp", caption: "Sheet-Metal & Mechanical Integration" },
  { type: "image", src: "/assets/service/IMG_3579.webp", caption: "Kewanee Boiler · Preventative Service" },
  { type: "image", src: "/assets/service/IMG_1840.webp", caption: "Riser Assembly · Groove-Fit Coupling" },
  { type: "image", src: "/assets/service/IMG_2674.webp", caption: "Painted Hydronic Piping · Site Handover" },
  { type: "image", src: "/assets/service/IMG_4432.webp", caption: "RTU Air Lift · Rooftop Placement" },
  { type: "image", src: "/assets/service/IMG_6948.webp", caption: "The Anron Crew · North Babylon, NY" },
  { type: "image", src: "/assets/service/IMG_4040.webp", caption: "Rooftop Chillers · Field Fabrication" },
  { type: "image", src: "/assets/service/IMG_6600.webp", caption: "Manhattan High-Rise · Precision Rigging" },
  { type: "image", src: "/assets/service/IMG_5998.webp", caption: "Leviton HQ · Winter Set" },
  { type: "image", src: "/assets/service/IMG_0111.webp", caption: "Rooftop RTU · Crane Setdown" },
  { type: "image", src: "/assets/service/image000000.JPG", caption: "Anron Mobile Fleet · Local 638 & Local 28" },
];

const ADVANCE_MS = 5500;

export default function ServiceCarousel() {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    clearTimeout(timerRef.current);
    if (paused) return;
    timerRef.current = setTimeout(() => {
      setActive((i) => (i + 1) % SLIDES.length);
    }, ADVANCE_MS);
    return () => clearTimeout(timerRef.current);
  }, [active, paused]);

  const goTo = (i) => setActive(((i % SLIDES.length) + SLIDES.length) % SLIDES.length);
  const next = () => goTo(active + 1);
  const prev = () => goTo(active - 1);

  return (
    <section
      data-testid="service-carousel"
      className="relative h-[70vh] md:h-[88vh] w-full overflow-hidden bg-black border-b border-white/10 group"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {/* Slides */}
      <div className="absolute inset-0">
        {SLIDES.map((s, i) => {
          const isActive = i === active;
          return (
            <div
              key={s.src}
              data-testid={`service-slide-${i}`}
              className={`absolute inset-0 transition-opacity duration-[1400ms] ease-out ${
                isActive ? "opacity-100 z-10" : "opacity-0 z-0"
              }`}
            >
              {s.type === "image" ? (
                <img
                  src={s.src}
                  alt={s.caption}
                  loading={i < 2 ? "eager" : "lazy"}
                  className="w-full h-full object-cover"
                />
              ) : null}
              {/* Cinematic vignette */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/10 to-black/30 pointer-events-none" />
              <div className="absolute inset-0 bg-gradient-to-r from-black/50 via-transparent to-transparent pointer-events-none" />
            </div>
          );
        })}
      </div>

      {/* Bottom UI */}
      <div className="absolute inset-x-0 bottom-0 z-20 px-4 md:px-8 pb-6 md:pb-10 flex flex-col md:flex-row md:items-end md:justify-between gap-4 md:gap-8">
        {/* Left: index only */}
        <div className="flex items-end gap-4 md:gap-6">
          <span
            data-testid="service-carousel-index"
            className="font-mono text-[10px] md:text-xs uppercase tracking-[0.3em] text-white/70"
          >
            {String(active + 1).padStart(2, "0")}
            <span className="text-white/30"> / {String(SLIDES.length).padStart(2, "0")}</span>
          </span>
        </div>

        {/* Right: prev / next + progress */}
        <div className="flex items-center gap-3 md:gap-4">
          <button
            type="button"
            onClick={prev}
            data-testid="service-carousel-prev"
            aria-label="Previous slide"
            className="w-10 h-10 md:w-12 md:h-12 border border-white/30 hover:border-white text-white/80 hover:text-white transition-all flex items-center justify-center font-mono text-sm hover:bg-white/10"
          >
            ←
          </button>
          <button
            type="button"
            onClick={next}
            data-testid="service-carousel-next"
            aria-label="Next slide"
            className="w-10 h-10 md:w-12 md:h-12 border border-white/30 hover:border-white text-white/80 hover:text-white transition-all flex items-center justify-center font-mono text-sm hover:bg-white/10"
          >
            →
          </button>
        </div>
      </div>

      {/* Progress bars strip */}
      <div className="absolute top-0 left-0 right-0 z-20 flex gap-1 px-4 md:px-8 pt-3 md:pt-4">
        {SLIDES.map((_, i) => (
          <button
            key={i}
            type="button"
            onClick={() => goTo(i)}
            aria-label={`Go to slide ${i + 1}`}
            data-testid={`service-carousel-dot-${i}`}
            className="flex-1 h-[2px] bg-white/15 overflow-hidden group/bar cursor-pointer"
          >
            <span
              className={`block h-full bg-white transition-transform origin-left ${
                i < active ? "scale-x-100" : i === active ? "scale-x-100 animate-progress" : "scale-x-0"
              }`}
              style={i === active ? { animationDuration: `${ADVANCE_MS}ms` } : {}}
            />
          </button>
        ))}
      </div>
    </section>
  );
}
